import { AUTH_EVENTS, postAuthEvent } from "../../utils/authChannel";
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { clearUserInfo, hideIdleModal, hideLoginModal, logout } from "../../store/auth/authSlice";
import { getShorthandName, isValidUrl } from "../../utils/Helper";
import { useNavigate } from "react-router-dom";
import { ReactComponent as DownArrowEnt } from "../../assets/icons/downArrowEnt.svg";
import { setClearClaimDetails } from "../../store/claim/claimSlice";
import { useLogOutUserMutation } from "../../store/auth/usersApiSlice";
import { useGetIndividualQuery, useSetDefaultCompanyMutation } from "../../store/Company/companyApiSlice";
import { setActiveCompany, selectActiveCompany } from "../../store/activeCompanySlice";
import { defaultOrMostPrivileged } from "../../utils/companyStatus";
import { ProfileMenu } from "../common/u1/ProfileMenu";
import { cn } from "../common/u1/cn";
import "../common/u1/tokens.css";

/* `className` merges into the root so a caller can change WHERE the chip shows
   without this component knowing about that screen. The dashboard app bar uses
   it to keep the avatar on a phone, where the public header still hides it. */
const UserProfileDropdown = ({ showDetails = false, className = "" }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    userInfo: { name, profile_image, email },
    token
  } = useSelector((state) => state.auth);
  const [logOutUser] = useLogOutUserMutation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isValidProfileImage, setIsValidProfileImage] = useState(false);

  // TIP-487: the user's companies + the active "acting as" company.
  const userInfo = useSelector((state) => state.auth.userInfo);
  const activeCompany = useSelector(selectActiveCompany);
  const individualID = userInfo?.individual?.[0]?.individualID;
  const { data: individualData, refetch: refetchIndividual } = useGetIndividualQuery(
    { individualID, token },
    { skip: !individualID }
  );
  // TIP-499: the user's persisted default company (companyID string or null).
  const defaultCompanyId = individualData?.default_company || null;
  const [setDefaultCompany] = useSetDefaultCompanyMutation();
  const companies = (individualData?.individual || [])
    .map((i) => ({
      companyID: i?.companyID?.companyID,
      name: i?.companyID?.registered_company_name,
      profile_status: i?.profile_status,
    }))
    .filter((c) => c.companyID);

  /* The navbar names the company being acted as — but only if it is still one
     of THIS user's companies. An account that manages none must show none, and
     a stale pick left by a previous session must not be presented as theirs. */
  const actingCompany =
    activeCompany && companies.some((c) => c.companyID === activeCompany.companyID)
      ? activeCompany
      : null;

  // First load: honour the user's chosen default (TIP-499); otherwise fall back
  // to the auto most-privileged pick (TIP-487).
  useEffect(() => {
    if (activeCompany) return;
    const list = (individualData?.individual || [])
      .map((i) => ({
        companyID: i?.companyID?.companyID,
        name: i?.companyID?.registered_company_name,
        profile_status: i?.profile_status,
      }))
      .filter((c) => c.companyID);
    if (list.length > 0) {
      dispatch(setActiveCompany(defaultOrMostPrivileged(list, individualData?.default_company)));
    }
  }, [activeCompany, individualData, dispatch]);

  const handleSelectCompany = (company) => {
    dispatch(setActiveCompany(company));
    setIsOpen(false);
  };

  // TIP-499: persist the chosen default per-Individual, then refetch so the
  // switcher's star marker reflects the saved choice.
  const handleSetDefaultCompany = async (company) => {
    try {
      await setDefaultCompany({ companyID: company.companyID, token }).unwrap();
      refetchIndividual();
    } catch (err) {
      // non-fatal — leave the existing default in place on failure
    }
  };

  useEffect(() => {
    if (profile_image) {
      const isValid = isValidUrl(profile_image);
      setIsValidProfileImage(isValid);
    }
  }, [profile_image])

  const handleDropdownToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    await logOutUser({ token })

    // Tell the other tabs before clearing this one. They share the cookies
    // that were just revoked, so without this they keep rendering a signed-in
    // UI until their next 401 — which on an idle page may never come.
    postAuthEvent(AUTH_EVENTS.SIGNED_OUT);
    //   credentials: "include", // 👈 VERY important

    dispatch(hideIdleModal());
    dispatch(logout());
    dispatch(hideLoginModal());
    dispatch(setClearClaimDetails())
    dispatch(clearUserInfo());
    navigate("/")
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={cn(
        "relative hidden sm:flex items-center gap-2 ml-6 cursor-pointer",
        /* Phone: the avatar alone. The design drops the name, the company and
           the chevron — the row has no width for them. */
        "max-[743px]:ml-0 max-[743px]:gap-0",
        className
      )}
      ref={dropdownRef}
      onClick={handleDropdownToggle}
    >
      <button
        className="flex items-center focus:outline-none bg-white rounded-full border-4 w-12 h-12 border-profile shadow-sm justify-center max-[743px]:h-9 max-[743px]:w-9 max-[743px]:border-2"
      >
        {profile_image && isValidProfileImage ? (
          <img src={profile_image} className="rounded-full" alt="user" />
        ) : (
          <div className="rounded-full flex items-center justify-center shadow-md cursor-pointer">
            <span
              className={`text-profile ${getShorthandName(name || "").length > 2 ? "text-f12" : "text-f14"
                }`}
            >
              {getShorthandName(name || "")}
            </span>
          </div>
        )}
      </button>

      {showDetails && (
        <div className="flex gap-2 items-center max-[743px]:hidden">
          <div className="text-left sm:hidden md:block">
            <p className="text-[14px] text-[#3C4848]">{name}</p>
            {/* TIP-487: navbar shows the active "acting as" company */}
            {actingCompany ? (
              <p className="text-[12px] text-[#777F7F] truncate max-w-[160px]">{actingCompany.name}</p>
            ) : (
              <p className="text-[12px] text-[#777F7F]">{email}</p>
            )}
          </div>
          {/* Points up while the menu is open, so the chevron says which way
              the control will go rather than only which way it opened. */}
          <span
            aria-hidden="true"
            className={`flex shrink-0 transition-transform duration-150 ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <DownArrowEnt />
          </span>
        </div>
      )}

      {isOpen && (
        /* TIP-506 — the panel anchors to the right edge in BOTH the dashboard
           and the public nav so it never runs off-screen. The stopPropagation
           stays: the whole wrapper is the toggle, so a click inside the panel
           would otherwise close it.

           `contents` is load-bearing. This element sits inside a flex row with
           a gap, so a normal wrapper becomes a zero-width flex item and adds
           one more gap the moment the menu opens — which shifted the whole
           account block sideways on every open. Generating no box of its own
           leaves the row untouched, and the absolutely-positioned panel still
           anchors to the `relative` wrapper above. */
        <div className="contents" onClick={(e) => e.stopPropagation()}>
          <ProfileMenu
            name={name}
            email={email}
            companies={companies}
            activeCompanyId={activeCompany?.companyID}
            defaultCompanyId={defaultCompanyId}
            onSelectCompany={handleSelectCompany}
            onSetDefault={handleSetDefaultCompany}
            onProfile={() => navigate("/dashboard/profile")}
            onLogout={handleLogout}
          />
        </div>
      )}
    </div>

  );
};

export default UserProfileDropdown;
